import { useState, useMemo, ReactNode } from 'react'

// Simple chevron icons
const ChevronUpIcon = ({ className }: { className: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
  </svg>
)

const ChevronDownIcon = ({ className }: { className: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
)

// Filter types
export type FilterType = 'text' | 'select' | 'date' | 'none'

export interface ColumnFilter {
  type: FilterType
  options?: Array<{ value: string; label: string }>
  placeholder?: string
  filterKey?: string // The field to filter on (might be different from sortKey)
}

export interface TableColumn<T> {
  key: string
  header: string
  sortKey?: keyof T
  render: (item: T) => ReactNode
  sortable?: boolean
  filterable?: ColumnFilter
  className?: string
}

interface TableProps<T> {
  data: T[]
  columns: TableColumn<T>[]
  searchableFields?: (keyof T)[]
  onRowClick?: (item: T) => void
  emptyMessage?: string
  emptySubMessage?: string
  className?: string
  searchPlaceholder?: string
  pageSize?: number
  t?: (key: string) => string
}

type SortDirection = 'asc' | 'desc' | null
type ColumnFilters = Record<string, string>

export function Table<T extends Record<string, any>>({
  data,
  columns,
  searchableFields = [],
  onRowClick,
  emptyMessage = "No items found",
  emptySubMessage = "Try adjusting your search or filters",
  className = "",
  // searchPlaceholder = "Search...",
  pageSize = 10,
  t
}: TableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortColumn, setSortColumn] = useState<keyof T | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({})

  // Filter data based on search term and column filters
  const filteredData = useMemo(() => {
    let filtered = data

    // Apply global search
    if (searchTerm && searchableFields.length > 0) {
      filtered = filtered.filter(item =>
        searchableFields.some(field => {
          const value = item[field]
          if (value == null) return false
          return String(value).toLowerCase().includes(searchTerm.toLowerCase())
        })
      )
    }

    // Apply column filters
    Object.entries(columnFilters).forEach(([columnKey, filterValue]) => {
      if (!filterValue) return

      const column = columns.find(col => col.key === columnKey)
      if (!column?.filterable) return

      const filterKey = column.filterable.filterKey || (column.sortKey as string)
      if (!filterKey) return

      filtered = filtered.filter(item => {
        let itemValue = item[filterKey as keyof T]
        
        // Handle nested properties for frequency filtering
        if (filterKey === 'defaultFrequency' && itemValue && typeof itemValue === 'object' && 'mode' in itemValue) {
          itemValue = (itemValue as any).mode || 'not set'
        }
        
        if (itemValue == null) return false

        const itemStr = String(itemValue).toLowerCase()
        const filterStr = filterValue.toLowerCase()

        if (column.filterable?.type === 'select') {
          return itemStr === filterStr
        } else {
          return itemStr.includes(filterStr)
        }
      })
    })

    return filtered
  }, [data, searchTerm, searchableFields, columnFilters, columns])

  // Sort filtered data
  const sortedData = useMemo(() => {
    if (!sortColumn || !sortDirection) {
      return filteredData
    }

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortColumn]
      const bValue = b[sortColumn]

      if (aValue == null && bValue == null) return 0
      if (aValue == null) return sortDirection === 'asc' ? -1 : 1
      if (bValue == null) return sortDirection === 'asc' ? 1 : -1

      const aStr = String(aValue).toLowerCase()
      const bStr = String(bValue).toLowerCase()

      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1
      if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredData, sortColumn, sortDirection])

  // Paginate sorted data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return sortedData.slice(startIndex, startIndex + pageSize)
  }, [sortedData, currentPage, pageSize])

  const totalPages = Math.ceil(sortedData.length / pageSize)

  const handleSort = (column: TableColumn<T>) => {
    if (!column.sortable || !column.sortKey) return

    if (sortColumn === column.sortKey) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortDirection(null)
        setSortColumn(null)
      }
    } else {
      setSortColumn(column.sortKey)
      setSortDirection('asc')
    }
    setCurrentPage(1) // Reset to first page when sorting
  }

  const handleColumnFilter = (columnKey: string, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnKey]: value
    }))
    setCurrentPage(1) // Reset to first page when filtering
  }

  const clearColumnFilter = (columnKey: string) => {
    setColumnFilters(prev => {
      const newFilters = { ...prev }
      delete newFilters[columnKey]
      return newFilters
    })
  }

  const clearAllFilters = () => {
    setSearchTerm('')
    setColumnFilters({})
    setCurrentPage(1)
  }

  const getSortIcon = (column: TableColumn<T>) => {
    if (!column.sortable || !column.sortKey) return null
    
    if (sortColumn !== column.sortKey) {
      return <ChevronUpIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
    }
    
    if (sortDirection === 'asc') {
      return <ChevronUpIcon className="w-4 h-4 text-blue-600" />
    } else if (sortDirection === 'desc') {
      return <ChevronDownIcon className="w-4 h-4 text-blue-600" />
    }
    
    return <ChevronUpIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
  }

  const renderColumnFilter = (column: TableColumn<T>) => {
    if (!column.filterable || column.filterable.type === 'none') return null

    const filterValue = columnFilters[column.key] || ''

    switch (column.filterable.type) {
      case 'text':
        return (
          <div className="relative">
            <input
              type="text"
              placeholder={column.filterable.placeholder || (t ? t('filters.filterBy.generic') : `Filter ${column.header.toLowerCase()}...`)}
              value={filterValue}
              onChange={(e) => handleColumnFilter(column.key, e.target.value)}
              onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking input
              className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white h-8"
            />
            {filterValue && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  clearColumnFilter(column.key)
                }}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
              >
                ✕
              </button>
            )}
          </div>
        )
      
      case 'select':
        return (
          <select
            value={filterValue}
            onChange={(e) => handleColumnFilter(column.key, e.target.value)}
            onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking select
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 h-8"
          >
            <option key={`all-${column.key}`} value="">{t ? t(`filters.all.${column.key}`) : `All ${column.header}`}</option>
            {column.filterable.options?.map((option) => (
              <option key={`${column.key}-${option.value}`} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )
      
      case 'date':
        return (
          <div className="relative">
            <input
              type="date"
              value={filterValue}
              onChange={(e) => handleColumnFilter(column.key, e.target.value)}
              onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking input
              className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white h-8"
            />
            {filterValue && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  clearColumnFilter(column.key)
                }}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
              >
                ✕
              </button>
            )}
          </div>
        )
      
      default:
        return null
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
  }

  const hasActiveFilters = searchTerm || Object.keys(columnFilters).length > 0
  // const activeFiltersCount = Object.values(columnFilters).filter(Boolean).length + (searchTerm ? 1 : 0)

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-500 text-lg">{emptyMessage}</p>
        <p className="text-gray-400 mt-2">{emptySubMessage}</p>
      </div>
    )
  }

  return (
    <div className={`bg-white rounded-lg shadow overflow-hidden ${className}`}>
      

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            {/* Header Row */}
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    column.sortable ? 'cursor-pointer select-none group hover:bg-gray-100' : ''
                  } ${column.className || ''}`}
                  onClick={() => handleSort(column)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.header}</span>
                    {getSortIcon(column)}
                  </div>
                </th>
              ))}
            </tr>
            {/* Filter Row */}
            <tr className="bg-gray-25">
              {columns.map((column) => (
                <th key={`filter-${column.key}`} className="px-3 py-2">
                  {renderColumnFilter(column)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  <div className="text-gray-500">
                    <p className="text-lg">{emptyMessage}</p>
                    <p className="text-sm mt-2">{emptySubMessage}</p>
                    {hasActiveFilters && (
                      <button
                        onClick={clearAllFilters}
                        className="mt-4 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        {t?.('filters.clearFilters') || 'Clear all filters'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              paginatedData.map((item, index) => (
                <tr
                  key={item.id || index}
                  className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick?.(item)}
                >
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`px-3 py-4 ${column.key === 'challenge' ? 'whitespace-normal' : 'whitespace-nowrap'} ${column.className || ''}`}
                    >
                      {column.render(item)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200 bg-gray-50">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t?.('common.previous') || 'Previous'}
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t?.('common.next') || 'Next'}
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                {t?.('filters.showing') || 'Showing'}{' '}
                <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span>
                {' '}{t?.('filters.of') || 'to'}{' '}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, sortedData.length)}
                </span>
                {' '}{t?.('filters.of') || 'of'}{' '}
                <span className="font-medium">{sortedData.length}</span>
                {' '}{t?.('filters.results') || 'results'}
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-full shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronUpIcon className="h-5 w-5 transform -rotate-90" aria-hidden="true" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNum
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronUpIcon className="h-5 w-5 transform rotate-90" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 